import React, { useEffect, useState, useRef } from "react";
import defaultTemplateImage from "../../assets/images/template-default.png"; // default card image
import TemplateService from "../../services/TemplateService.js";
import { useEventStore } from "../../store/eventStore";

// Card Component
const TemplateCard = ({ template, onSelect, isSelected }) => {
  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

  const backgroundImage = template.templateImage
    ? `${BASE_URL}${template.templateImage}`
    : defaultTemplateImage;

  const cardRef = useRef(null);
  const templateBodyRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 900, height: 400 });

  // Load template image to get actual dimensions
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = backgroundImage;
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      setImageSize({ width: 900, height: 400 }); // fallback
    };
  }, [backgroundImage]);

  // Calculate scale based on actual image dimensions
  useEffect(() => {
    if (!cardRef.current || imageSize.width === 0 || imageSize.height === 0) return;
    
    const computeScale = () => {
      const { width, height } = cardRef.current.getBoundingClientRect();
      // Calculate scale to fit within card while maintaining aspect ratio
      const scaleX = width / imageSize.width;
      const scaleY = height / imageSize.height;
      const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
      setScale(newScale);
    };

    // measure on mount
    computeScale();

    // monitor size changes (responsive layouts can shrink the card)
    const observer = new ResizeObserver(computeScale);
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [imageSize]);

  // Ensure text doesn't overflow in shapes and auto-adjust font size
  useEffect(() => {
    if (!templateBodyRef.current) return;
    
    const container = templateBodyRef.current;
    const shapes = container.querySelectorAll('div[style*="position: absolute"]');
    
    shapes.forEach((shape) => {
      // Ensure shape container clips content
      if (!shape.style.overflow || shape.style.overflow === 'visible') {
        shape.style.overflow = 'hidden';
      }
      
      // Get shape dimensions
      const shapeRect = shape.getBoundingClientRect();
      const shapeWidth = shapeRect.width;
      const shapeHeight = shapeRect.height;
      
      // Ensure text spans don't overflow and auto-adjust font size
      const spans = shape.querySelectorAll('span');
      spans.forEach((span) => {
        span.style.overflow = 'hidden';
        span.style.textOverflow = 'ellipsis';
        span.style.maxWidth = '100%';
        span.style.maxHeight = '100%';
        span.style.display = 'block';
        span.style.wordWrap = 'break-word';
        span.style.wordBreak = 'break-word';
        
        // Auto-adjust font size if text overflows
        if (shapeWidth > 0 && shapeHeight > 0) {
          let fontSize = parseFloat(span.style.fontSize) || parseFloat(window.getComputedStyle(span).fontSize) || 24;
          const originalFontSize = fontSize;
          const minFontSize = 8;
          const padding = 10; // Account for padding/margins
          const availableWidth = shapeWidth - padding;
          const availableHeight = shapeHeight - padding;
          
          // Create a temporary element to measure text with word wrap
          const tempSpan = span.cloneNode(true);
          tempSpan.style.visibility = 'hidden';
          tempSpan.style.position = 'absolute';
          tempSpan.style.top = '-9999px';
          tempSpan.style.left = '-9999px';
          tempSpan.style.width = `${availableWidth}px`;
          tempSpan.style.whiteSpace = 'normal';
          tempSpan.style.wordWrap = 'break-word';
          tempSpan.style.wordBreak = 'break-word';
          document.body.appendChild(tempSpan);
          
          // Reduce font size until text fits
          while (fontSize > minFontSize) {
            tempSpan.style.fontSize = `${fontSize}px`;
            const textHeight = tempSpan.scrollHeight;
            
            // Check if text fits within available height
            if (textHeight <= availableHeight) {
              break;
            }
            
            fontSize = Math.max(minFontSize, fontSize * 0.9); // Reduce by 10%
          }
          
          document.body.removeChild(tempSpan);
          
          // Apply the adjusted font size if it was reduced
          if (fontSize < originalFontSize) {
            span.style.fontSize = `${fontSize}px`;
          }
        }
      });
    });
  }, [template.templateBody, scale]);

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center" }}>
      {/* Card */}
      <div
        ref={cardRef}
        className={`border rounded-t-lg shadow-sm cursor-pointer flex-shrink-0 ${
          isSelected ? "ring-2 ring-blue-500" : ""
        }`}
        style={{
          width: "100%",
          height: "280px",
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "relative",
        }}
        onClick={() => onSelect(template)}
      >
        {/* inner wrapper is scaled down to fit card while maintaining aspect ratio */}
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: `${imageSize.width}px`,
            height: `${imageSize.height}px`,
            position: "absolute",
            top: 0,
            left: 0,
            overflow: "hidden",
          }}
        >
          <div
            ref={templateBodyRef}
            dangerouslySetInnerHTML={{ __html: template.templateBody }}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              width: "100%",
              height: "100%",
              position: "relative",
              padding: 0,
              margin: 0,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          />
        </div>
      </div>

      {/* Name Bar */}
      <div
        style={{
          width: "280px",
          border: "1px solid #ccc",
          borderTop: "none",
          textAlign: "center",
          padding: "8px 0",
          backgroundColor: "#f9f9f9",
          fontSize: "16px",
          fontWeight: "500",
        }}
      >
        {template.name}
      </div>
    </div>
  );
};

// Section Component
const TemplateSection = ({ title, templates, onSelect, selectedId }) => {
  // Group by category
  const grouped = templates.reduce((acc, t) => {
    acc[t.templateCategory] = acc[t.templateCategory] || [];
    acc[t.templateCategory].push(t);
    return acc;
  }, {});

  return (
    <div style={{ marginBottom: "30px" }}>
      <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "12px" }}>
        {title}
      </h2>

      {Object.keys(grouped).map((category) => (
        <div key={category} style={{ marginBottom: "20px" }}>
          <h3
            style={{
              fontSize: "15px",
              fontWeight: "500",
              marginBottom: "8px",
              color: "#333",
            }}
          >
            {category}
          </h3>

          <div
            style={{
              display: "flex",
              gap: "10px",
              overflowX: "auto",
              paddingBottom: "5px",
            }}
          >
            {grouped[category].map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                isSelected={selectedId === template.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Component
const ChooseTemplateTab = ({ onTemplateSelect }) => {
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedTemplate = useEventStore((state) => state.selectedTemplate);
  const templateId = useEventStore((state) => state.eventDetails.templateId);
  const setTemplate = useEventStore((state) => state.setTemplate);

  const selectedId = selectedTemplate?.id || templateId || null;

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await TemplateService.fetchTemplates();
        let data = [];

        if (response.data?.success && Array.isArray(response.data.data)) {
          data = response.data.data.map((template) => ({
            name: template.name,
            templateBody: template.templateBody,
            templateDetails: template.templateDetails,
            templateCategory: template.templateCategory,
            templateImage: template.templateImage,
            id: template.id,
          }));
        } else {
          console.error("API did not return expected data format");
        }

        // Split into saved and default
        setSavedTemplates(data.slice(0, 2));
        setDefaultTemplates(data);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };

    fetchTemplates();
  }, []);

  const handleSelect = (template) => {
    setTemplate(template);

    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
  };

  const filterTemplates = (list) => {
    if (!searchTerm.trim()) return list;
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.templateCategory.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div style={{ padding: "20px", fontFamily: "sans-serif" }}>
      {/* Search Bar */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: "999px",
          padding: "4px 12px",
          backgroundColor: "#fff",
          width: "33%",
          minWidth: "250px",
          marginLeft: "auto",
        }}
      >
        <i
          className="bi bi-list"
          style={{
            marginRight: "8px",
            color: "#777",
            fontSize: "18px",
          }}
        ></i>

        <input
          type="text"
          placeholder="Search by name or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: "14px",
          }}
        />

        <button
          onClick={() => console.log("Search clicked")}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: "16px",
            color: "#777",
          }}
        >
          <i className="bi bi-search"></i>
        </button>
      </div>

      {/* Saved Templates */}
      <TemplateSection
        title="Saved Templates"
        templates={filterTemplates(savedTemplates)}
        onSelect={handleSelect}
        selectedId={selectedId}
      />

      <hr
        style={{
          margin: "30px 0",
          border: "none",
          borderTop: "2px solid #e0e0e0",
        }}
      />

      {/* Default Templates */}
      <TemplateSection
        title="Default Templates"
        templates={filterTemplates(defaultTemplates)}
        onSelect={handleSelect}
        selectedId={selectedId}
      />
    </div>
  );
};

export default ChooseTemplateTab;
